import { useEffect, useRef } from 'react'
import { useSetAtom } from 'jotai'
import { GitCompare } from 'lucide-react'
import { openMainPanelOverlayAtom } from '@/stores/mainPanelOverlayStore'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
} from './_base'

const completedIcon = (
  <GitCompare className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
)

function arrayFrom(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value : []
}

function summarizeText(value: unknown, max = 120): string | null {
  const text = formatToolValue(value)
  if (!text) return null
  return text.length > max ? `${text.slice(0, max).trim()}...` : text
}

function formatCount(n: number, singular: string): string {
  return `${n} ${n === 1 ? singular : `${singular}s`}`
}

function draftDetail(result: any): string | null {
  const draft = result?.draft
  if (!draft) return null
  const sectionCount = arrayFrom(draft.sectionEdits).length
  const claimCount = arrayFrom(draft.claimDiff).length
  return [
    sectionCount > 0 ? formatCount(sectionCount, 'section') : null,
    claimCount > 0 ? formatCount(claimCount, 'claim change') : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

function acceptDetail(result: any): string | null {
  const sectionsEdited =
    typeof result?.sectionsEdited === 'number' ? result.sectionsEdited : null
  const claimsAdded =
    typeof result?.claimsAdded === 'number' ? result.claimsAdded : null
  const claimsSuperseded =
    typeof result?.claimsSuperseded === 'number'
      ? result.claimsSuperseded
      : null
  const parts = [
    sectionsEdited !== null ? formatCount(sectionsEdited, 'section') : null,
    claimsAdded !== null ? `${claimsAdded} added` : null,
    claimsSuperseded !== null ? `${claimsSuperseded} superseded` : null,
  ].filter(Boolean)
  return parts.length > 0 ? parts.join(' · ') : null
}

export function DriftProposeToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const failed = state.isError || isToolError(state)
  const draft = state.result?.draft
  const sectionEdits = arrayFrom(draft?.sectionEdits)
  const claimDiff = arrayFrom(draft?.claimDiff)
  const openOverlay = useSetAtom(openMainPanelOverlayAtom)
  const openedOverlayRef = useRef(false)
  const policySlug = pickString(state.args?.policySlug)

  useEffect(() => {
    if (
      !state.isComplete ||
      failed ||
      !draft ||
      !policySlug ||
      openedOverlayRef.current
    ) {
      return
    }
    openedOverlayRef.current = true
    openOverlay({
      kind: 'policy-drift-review',
      id: getToolCallId(part, `policy-drift-review-${policySlug}`),
      source: 'chat',
      payload: {
        policySlug,
        rationale: formatToolValue(draft.rationale),
        confidence: formatToolValue(draft.confidence),
        autoApplyEligible:
          typeof state.result?.autoApplyEligible === 'boolean'
            ? state.result.autoApplyEligible
            : null,
        sectionEdits: sectionEdits.map((edit) => ({
          sectionKey: formatToolValue(edit.sectionKey),
          section: formatToolValue(edit.section),
          replacementText: formatToolValue(edit.replacementText),
        })),
        claimDiff: claimDiff.map((change) => ({
          action: formatToolValue(change.action),
          claimText: formatToolValue(change.claimText),
          controlRef: formatToolValue(change.controlRef),
        })),
      },
    })
  }, [
    claimDiff,
    draft,
    failed,
    openOverlay,
    part,
    policySlug,
    sectionEdits,
    state.isComplete,
    state.result?.autoApplyEligible,
  ])

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Drafting policy drift fix...'
        completedLabel='Policy drift draft ready'
        loadingDetail={pickString(state.args?.policySlug)}
        completedDetail={
          failed
            ? pickString(state.args?.policySlug)
            : draftDetail(state.result)
        }
        completedIcon={completedIcon}
      />
      {state.isComplete && !failed ? (
        <ToolListPreview
          emptyLabel='No section edits proposed'
          items={sectionEdits.map((edit) => ({
            id: pickString(edit.sectionKey, edit.section),
            title: pickString(edit.sectionKey, edit.section) ?? 'Section edit',
            subtitle: summarizeText(edit.replacementText),
            meta: [
              draft?.confidence,
              claimDiff.length > 0
                ? plural(claimDiff.length, 'claim change')
                : null,
              state.result?.autoApplyEligible ? 'Auto-apply eligible' : null,
            ],
            tone: normalizeTone(draft?.confidence),
          }))}
        />
      ) : null}
    </>
  )
}

export function DriftAcceptToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const failed = state.isError || isToolError(state)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Applying policy drift fix...'
      completedLabel='Policy drift fix applied'
      loadingDetail={pickString(state.args?.policySlug)}
      completedDetail={
        failed
          ? pickString(state.args?.policySlug, state.args?.findingKey)
          : acceptDetail(state.result)
      }
      completedIcon={completedIcon}
    />
  )
}

export function DriftDismissToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const failed = state.isError || isToolError(state)
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Dismissing policy drift...'
      completedLabel='Policy drift dismissed'
      loadingDetail={pickString(state.args?.findingKey)}
      completedDetail={failed ? pickString(state.args?.findingKey) : null}
      completedIcon={completedIcon}
    />
  )
}
